import React, { useEffect, useState, useContext, KeyboardEvent } from 'react';
import { Alert } from '@mui/material';
import Button from '@components/Button';
import { Comment } from '@models/Comment';
import useComments, { CommentValue, NewCommentValue } from '@hooks/useComments';
import CommentItem, { UpdateCommentProps } from '@components/experiments/comments/CommentItem';
import { ScrollableSidebarContent } from '@components/experiments/ScrollableSidebarContent';
import ButtonGroup, { ButtonGroupItem } from '@components/ButtonGroup';
import cn from 'classnames';
import { LoadingIndicator } from '../../LoadingButton';
import PreprocessStep from '@models/PreprocessStep';
import { MentionsInput, Mention, OnChangeHandlerFunc } from 'react-mentions';
import useCommentMentions, { defaultMentionInputStyle, defaultMentionStyle } from '@/src/hooks/useCommentMentions';
import { AuthContext } from '@contexts/AuthContext';
import { isSponsor } from '@models/User';
import PrivateCommentCheckbox from './PrivateCommentCheckbox';

type Props = { preprocess: PreprocessStep };
const PreprocessCommentsView = ({ preprocess }: Props) => {
    const { user } = useContext(AuthContext);
    const isSponsorUser = isSponsor(user);
    const [selectedTab, setSelectedTab] = useState<CommentValue>('comment,note');
    const [newComment, setNewComment] = useState<string>('');
    const [newCommentType, setNewCommentType] = useState<NewCommentValue>('comment');

    const {
        comments,
        postNewComment,
        deleteComment,
        updateComment,
        updateCommentType,
        commentError,
        replyToComment,
        commentsLoading,
    } = useComments({
        object_type: preprocess.preprocess_type.object_type,
        object_uuid: preprocess.uuid,
    });
    const { orgUsers } = useCommentMentions();
    const [isPrivate, setIsPrivate] = useState<boolean>(false);

    useEffect(() => {
        updateCommentType(selectedTab);
        if (selectedTab === 'comment,note') return;
        if (selectedTab) setNewCommentType(selectedTab);
    }, [selectedTab]);

    const handleChangeComment: OnChangeHandlerFunc = (_, newValue) => {
        setNewComment(newValue);
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement> | KeyboardEvent<HTMLInputElement>) => {
        if (!event.shiftKey && event.key === 'Enter') {
            event.preventDefault();
            if (!(event.target as HTMLTextAreaElement).value) return;

            return handleCreateNewComment();
        }
    };

    const handleCreateNewComment = () => {
        postNewComment({ content: newComment, comment_type: newCommentType, is_private: isSponsorUser || isPrivate });
        setNewComment('');
        setIsPrivate(false);
    };

    const handleDeleteComment = (comment_uuid: string) => deleteComment({ comment_uuid });

    type ReplyCommentProps = { parent_comment: Comment; content: string };
    const handleUpdateComment = ({ comment_uuid, content, comment_type }: UpdateCommentProps) =>
        updateComment({ comment_uuid, content, comment_type: comment_type as NewCommentValue });

    const handleReplyToComment = ({ parent_comment, content }: ReplyCommentProps) =>
        replyToComment({ parent_comment, content });

    const newCommentTabs: ButtonGroupItem<NewCommentValue>[] = [
        { value: 'comment', label: 'Comment' },
        { value: 'note', label: 'Note' },
    ];

    const tabs: ButtonGroupItem<CommentValue>[] = [
        { value: 'comment,note', label: 'All' },
        { value: 'comment', label: 'Comments' },
        { value: 'note', label: 'Notes' },
    ];

    const CommentFilterOption = ({ tab }: { tab: ButtonGroupItem<NewCommentValue> }) => (
        <div
            className={cn('', { 'cursor-pointer': newCommentType !== tab.value })}
            onClick={() => setNewCommentType(tab.value)}
        >
            <p
                className={cn('mb-1 font-semibold tracking-tight', {
                    'text-primary': newCommentType !== tab.value,
                    'text-slate-400': newCommentType === tab.value,
                })}
            >
                {tab.label}
            </p>
        </div>
    );

    const commentTypeText = selectedTab === 'note' ? 'notes' : 'comment';
    if (!preprocess || !user) return <></>;
    return (
        <>
            {commentError ? (
                <div className="m-2 mb-0 rounded">
                    <Alert severity="error">{commentError}</Alert>
                </div>
            ) : null}
            {commentsLoading ? (
                <div className="absolute left-0 top-0 flex h-full w-full items-center justify-center bg-white opacity-50">
                    <LoadingIndicator size={48} />
                </div>
            ) : null}
            <div className="my-2 flex flex-col p-4 pb-2 pl-8">
                <div className="mb-2 flex overflow-x-auto px-8 pb-2">
                    <ButtonGroup
                        items={tabs}
                        onChange={(screen) => setSelectedTab(screen ?? 'comment,note')}
                        toggle={false}
                        value={selectedTab}
                    />
                </div>
                <div className="flex flex-col">
                    {selectedTab === 'comment,note' ? (
                        <div className="flex space-x-2">
                            {newCommentTabs.map((tab: ButtonGroupItem<NewCommentValue>, index: number) => (
                                <CommentFilterOption tab={tab} key={index} />
                            ))}
                        </div>
                    ) : null}

                    <MentionsInput
                        className={cn('mb-2', {
                            'bg-indigo-50': newCommentType === 'note',
                        })}
                        style={defaultMentionInputStyle}
                        value={newComment}
                        onChange={handleChangeComment}
                        onKeyDown={handleKeyDown}
                        placeholder={`Add a ${newCommentType === 'comment' ? 'comment' : 'note'}...`}
                        allowSpaceInQuery
                    >
                        <Mention
                            trigger="@"
                            data={orgUsers}
                            style={defaultMentionStyle}
                            markup="@{__id__}"
                            displayTransform={(id: string) =>
                                orgUsers?.find((mention) => mention.id === id)?.display || id
                            }
                        />
                    </MentionsInput>

                    <PrivateCommentCheckbox value={isPrivate} onChange={setIsPrivate} type={newCommentType} />

                    <div className="flex justify-between">
                        <div className="text-[11px] text-gray-400 pt-1">markdown supported</div>
                        <div>
                            <Button
                                className="self-end !py-[2px]"
                                variant="contained"
                                color="primary"
                                onClick={handleCreateNewComment}
                                disabled={!newComment}
                                size="small"
                            >
                                Save
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
            {comments && comments.length ? (
                <ScrollableSidebarContent className="mb-4 pl-8 pr-4">
                    <div className="flex flex-col">
                        {comments?.map((comment: Comment) => (
                            <CommentItem
                                key={comment.uuid}
                                comment={comment}
                                handleDelete={handleDeleteComment}
                                handleReply={handleReplyToComment}
                                handleUpdate={handleUpdateComment}
                                userId={user.uuid ?? ''}
                                commentObjectType={preprocess.preprocess_type.object_type}
                            />
                        ))}
                    </div>
                </ScrollableSidebarContent>
            ) : (
                <p className="ml-8 text-default">
                    No {commentTypeText} yet. Be the first to share your thoughts and observations.
                </p>
            )}
        </>
    );
};

export default PreprocessCommentsView;
